import { useState, useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Play, Send, ChevronRight, Mail, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Navbar, Footer } from '@/components/Layout';
import { CarCard } from '@/components/CarCard';
import { SearchFilters } from '@/components/SearchFilters';
import { FAQSection, BrandsSection, ServicesSection, TeamSection, BlogSection } from '@/components/HomeModules';
import { carsApi, contactApi, seedApi, newsletterApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useTranslatedSettings } from '@/context/SiteSettingsContext';
import { useLanguage } from '@/context/LanguageContext';
import { Helmet } from 'react-helmet';
import TurnstileWidget from '@/components/TurnstileWidget';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Default module order (fallback)
const DEFAULT_MODULE_ORDER = ['featured', 'brands', 'about', 'video', 'services', 'team', 'blog', 'newsletter', 'contact', 'faq'];

const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${API_URL}${url}`;
};

// Extract YouTube Video ID from URL
const extractYouTubeId = (url) => {
  if (!url) return '';
  const regExp = /^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;
  const match = url.match(regExp);
  return (match && match[7].length === 11) ? match[7] : '';
};

// Newsletter Section Component
function NewsletterSection() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const { t } = useLanguage();

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!email) return;

    setSubscribing(true);
    try {
      const response = await newsletterApi.subscribe(email, name || null, 'website');
      if (response.data.already_subscribed) {
        toast.info(t('newsletter_already'));
      } else {
        setSubscribed(true);
        toast.success(t('newsletter_success'));
        setEmail('');
        setName('');
      }
    } catch (error) {
      toast.error(t('error'));
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <section className="py-16 md:py-24 bg-theme-surface" id="newsletter" data-testid="newsletter-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl mx-auto text-center"
        >
          <div className="w-16 h-16 bg-gold/10 rounded-full flex items-center justify-center mx-auto mb-6">
            <Mail size={28} className="text-primary" />
          </div>
          <p className="overline mb-2">{t('newsletter_subtitle')}</p>
          <h2 className="font-heading text-3xl md:text-4xl font-light mb-4 text-theme">
            {t('newsletter_title')}
          </h2>
          <p className="text-theme-muted font-light mb-8">
            {t('newsletter_description')}
          </p>

          {subscribed ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-green-500/10 border border-green-500/30 rounded-lg p-6"
            >
              <CheckCircle size={40} className="text-green-500 mx-auto mb-3" />
              <p className="text-theme font-medium">{t('newsletter_success')}</p>
              <p className="text-theme-muted text-sm mt-1">{t('newsletter_success_sub')}</p>
            </motion.div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col sm:flex-row gap-4 max-w-xl mx-auto">
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder={t('newsletter_name')}
                className="input-premium flex-1"
              />
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('newsletter_email')}
                className="input-premium flex-1"
                required
                data-testid="newsletter-email"
              />
              <Button
                type="submit"
                className="btn-primary flex items-center justify-center gap-2 whitespace-nowrap"
                disabled={subscribing}
                data-testid="newsletter-submit"
              >
                {subscribing ? (
                  <div className="spinner" />
                ) : (
                  <>
                    {t('newsletter_subscribe')}
                    <ArrowRight size={16} />
                  </>
                )}
              </Button>
            </form>
          )}

          <p className="text-xs text-theme-muted mt-6">
            {t('newsletter_privacy')}
          </p>
        </motion.div>
      </div>
    </section>
  );
}

export default function HomePage() {
  const [featuredCars, setFeaturedCars] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contactForm, setContactForm] = useState({ name: '', email: '', message: '' });
  const [sending, setSending] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const { settings } = useTranslatedSettings();
  const { t, language } = useLanguage();
  const location = useLocation();
  const s = settings || {};

  // Get sorted modules based on settings.modules_order
  const sortedModules = useMemo(() => {
    const order = s.modules_order || DEFAULT_MODULE_ORDER;
    // Ensure all default modules are included
    const allModules = [...order];
    DEFAULT_MODULE_ORDER.forEach(moduleId => {
      if (!allModules.includes(moduleId)) {
        allModules.push(moduleId);
      }
    });
    return allModules;
  }, [s.modules_order]);

  // Handle anchor scrolling when navigating from other pages
  useEffect(() => {
    if (location.hash) {
      // Wait for page to render
      const timer = setTimeout(() => {
        const element = document.querySelector(location.hash);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [location.hash]);

  // Track page view
  useEffect(() => {
    const trackPageView = async () => {
      try {
        let sessionId = localStorage.getItem('analytics_session');
        if (!sessionId) {
          sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
          localStorage.setItem('analytics_session', sessionId);
        }
        await fetch(`${API_URL}/api/analytics/track?page=homepage&session_id=${sessionId}`, {
          method: 'POST'
        });
      } catch (error) {
        console.log('Analytics tracking failed:', error);
      }
    };
    trackPageView();
  }, []);

  useEffect(() => {
    loadFeaturedCars();
  }, [s.featured_count]);

  useEffect(() => {
    loadBlogPosts();
  }, []);

  const loadBlogPosts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/blog/posts?published=true&limit=3`);
      if (response.ok) {
        const data = await response.json();
        setBlogPosts(data);
      }
    } catch (error) {
      console.log('Blog posts not available');
    }
  };

  const loadFeaturedCars = async () => {
    try {
      // First try to seed the database
      try {
        await seedApi.seed();
      } catch (e) {
        // Database might already be seeded
      }
      
      const limit = (settings && settings.featured_count) || 6;
      const response = await carsApi.getFeatured(limit);
      setFeaturedCars(response.data);
    } catch (error) {
      console.error('Error loading featured cars:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    
    // Verify Turnstile if configured
    if (s.turnstile_site_key && !turnstileToken) {
      toast.error(language === 'de' ? 'Bitte bestätigen Sie das CAPTCHA' : 'Please complete the CAPTCHA');
      return;
    }
    
    setSending(true);
    try {
      // Verify Turnstile token with backend if configured
      if (turnstileToken) {
        const verifyResponse = await fetch(`${API_URL}/api/captcha/verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: turnstileToken })
        });
        const verifyResult = await verifyResponse.json();
        if (!verifyResult.success) {
          toast.error(language === 'de' ? 'CAPTCHA-Überprüfung fehlgeschlagen' : 'CAPTCHA verification failed');
          setSending(false);
          return;
        }
      }
      
      await contactApi.submit(contactForm);
      toast.success(language === 'de' ? 'Nachricht erfolgreich gesendet! Wir melden uns bald bei Ihnen.' : 'Message sent successfully! We will get back to you soon.');
      setContactForm({ name: '', email: '', message: '' });
      setTurnstileToken(null);
    } catch (error) {
      toast.error(language === 'de' ? 'Fehler beim Senden. Bitte versuchen Sie es erneut.' : 'Error sending. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const handleFilter = (filters) => {
    // Build query string and navigate to cars page
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'all') params.append(key, value);
    });
    window.location.href = `/cars?${params.toString()}`;
  };

  const handleVideoClick = () => {
    if (s.video_url) {
      setVideoPlaying(true);
    }
  };
  
  // Extract YouTube video ID
  const extractVideoId = (url) => {
    if (!url) return null;
    const match = url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
    return match ? match[1] : null;
  };

  return (
    <div className="min-h-screen bg-theme">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{s.seo_title || 'Tim Automobile - Premium Fahrzeuge'}</title>
        <meta name="description" content={s.seo_description || 'Entdecken Sie unsere exklusive Auswahl an Premium- und Luxusfahrzeugen.'} />
        <meta name="keywords" content={s.seo_keywords || 'Luxusautos, Premium Fahrzeuge, Sportwagen'} />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content={s.site_name || 'Tim Automobile'} />
        <meta property="og:title" content={s.seo_title || 'Tim Automobile'} />
        <meta property="og:description" content={s.seo_description || 'Premium Fahrzeuge'} />
        {s.hero_image_url && <meta property="og:image" content={getImageUrl(s.hero_image_url)} />}
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={s.seo_title || 'Tim Automobile'} />
        <meta name="twitter:description" content={s.seo_description || 'Premium Fahrzeuge'} />
        {s.hero_image_url && <meta name="twitter:image" content={getImageUrl(s.hero_image_url)} />}
        
        {/* Structured Data - Organization */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoDealer",
            "name": s.site_name || "Tim Automobile",
            "description": s.seo_description || "Premium Fahrzeuge",
            "url": window.location.origin,
            "telephone": s.contact_phone || "+43 1 234 5678",
            "email": s.contact_email || "info@tim-automobile.at",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": s.contact_address || "Musterstraße 123, 1010 Wien"
            },
            "openingHours": [
              "Mo-Fr 09:00-18:00",
              "Sa 10:00-14:00"
            ]
          })}
        </script>
      </Helmet>

      <Navbar />

      {/* Hero Section */}
      <section className="relative h-screen min-h-[700px] overflow-hidden" data-testid="hero-section">
        {/* Background - Video or Image */}
        <div className="absolute inset-0">
          {s.hero_video_url ? (
            // YouTube Video Background - covers entire area without black bars
            <div className="absolute inset-0 w-full h-full overflow-hidden">
              <iframe
                src={`https://www.youtube.com/embed/${extractYouTubeId(s.hero_video_url)}?autoplay=1&mute=1&loop=1&playlist=${extractYouTubeId(s.hero_video_url)}&controls=0&showinfo=0&modestbranding=1&rel=0&iv_load_policy=3&disablekb=1&playsinline=1`}
                className="absolute top-1/2 left-1/2 border-0"
                style={{
                  width: '177.78vh', // 16:9 aspect ratio width based on viewport height
                  height: '56.25vw', // 16:9 aspect ratio height based on viewport width
                  minWidth: '100%',
                  minHeight: '100%',
                  transform: 'translate(-50%, -50%)',
                  pointerEvents: 'none'
                }}
                frameBorder="0"
                allow="autoplay; encrypted-media"
                title="Hero Video"
              />
            </div>
          ) : (
            // Image Background
            <img
              src={s.hero_image_url ? getImageUrl(s.hero_image_url) : "https://images.unsplash.com/photo-1544636331-e26879cd4d9b?w=1920&q=80"}
              alt="Luxury Car"
              className="w-full h-full object-cover"
            />
          )}
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col justify-center max-w-[1600px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <p className="overline mb-4" style={{ color: `${s.hero_subtitle_color || '#CCCCCC'}` }}>
              {s.site_tagline || 'Premium Automotive Experience'}
            </p>
            <h1 
              className="font-heading text-5xl sm:text-6xl lg:text-7xl font-light leading-tight mb-6"
              style={{ color: s.hero_text_color || '#FFFFFF' }}
            >
              {(s.header_title || 'Find Your Next Dream Car').split('\n').map((line, i) => (
                <span key={i}>
                  {i > 0 && <br />}
                  {i === 1 ? <span className="italic">{line}</span> : line}
                </span>
              ))}
            </h1>
            <p 
              className="text-lg md:text-xl font-light max-w-xl mb-8"
              style={{ color: s.hero_subtitle_color || '#CCCCCC' }}
            >
              {s.header_subtitle || 'Discover our curated collection of the world\'s most exceptional automobiles. Where passion meets performance.'}
            </p>
            <div className="flex flex-wrap gap-4">
              {s.hero_button_link?.startsWith('#') ? (
                <a 
                  href={s.hero_button_link || '#featured'} 
                  className="inline-flex items-center gap-2 px-6 py-3 rounded font-medium transition-all hover:opacity-90"
                  style={{ 
                    backgroundColor: s.hero_button_bg_color || '#FFFFFF',
                    color: s.hero_button_text_color || '#000000'
                  }}
                  data-testid="hero-browse-btn"
                >
                  {s.hero_button_text || s.nav_inventory || 'Fahrzeuge'}
                  <ArrowRight size={16} />
                </a>
              ) : (
                <Link 
                  to={s.hero_button_link || '/cars'} 
                  className="inline-flex items-center gap-2 px-6 py-3 rounded font-medium transition-all hover:opacity-90"
                  style={{ 
                    backgroundColor: s.hero_button_bg_color || '#FFFFFF',
                    color: s.hero_button_text_color || '#000000'
                  }}
                  data-testid="hero-browse-btn"
                >
                  {s.hero_button_text || s.nav_inventory || 'Fahrzeuge'}
                  <ArrowRight size={16} />
                </Link>
              )}
              {s.hero_button_secondary_link?.startsWith('#') ? (
                <a 
                  href={s.hero_button_secondary_link || '#about'} 
                  className="inline-flex items-center gap-2 px-6 py-3 rounded font-medium border-2 bg-transparent transition-all hover:opacity-80"
                  style={{ 
                    borderColor: s.hero_button_secondary_border_color || '#FFFFFF',
                    color: s.hero_button_secondary_text_color || '#FFFFFF'
                  }}
                >
                  {s.hero_button_secondary_text || 'Mehr erfahren'}
                </a>
              ) : (
                <Link 
                  to={s.hero_button_secondary_link || '/cars'} 
                  className="inline-flex items-center gap-2 px-6 py-3 rounded font-medium border-2 bg-transparent transition-all hover:opacity-80"
                  style={{ 
                    borderColor: s.hero_button_secondary_border_color || '#FFFFFF',
                    color: s.hero_button_secondary_text_color || '#FFFFFF'
                  }}
                >
                  {s.hero_button_secondary_text || 'Mehr erfahren'}
                </Link>
              )}
            </div>
          </motion.div>
        </div>

      </section>

      {/* Search & Filter Section */}
      <section className="py-16 md:py-20 -mt-32 relative z-20" data-testid="search-section">
        <div className="max-w-[1600px] mx-auto px-6 md:px-12">
          <SearchFilters onFilter={handleFilter} isHomePage={true} />
        </div>
      </section>

      {/* Dynamically Ordered Modules */}
      {sortedModules.map((moduleId) => {
        switch (moduleId) {
          case 'featured':
            return s.module_featured_cars_enabled !== false && (
              <section key="featured" id="featured" className="py-16 md:py-24" data-testid="featured-section">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                  <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-12">
                    <div>
                      <p className="overline mb-2">{s.featured_subtitle || t('featured_subtitle')}</p>
                      <h2 className="font-heading text-4xl md:text-5xl font-light">{s.featured_title || t('featured_title')}</h2>
                    </div>
                    <Link 
                      to="/cars" 
                      className="btn-ghost mt-4 md:mt-0 flex items-center gap-2"
                      data-testid="view-all-link"
                    >
                      {s.featured_view_all || t('featured_view_all')}
                      <ChevronRight size={16} />
                    </Link>
                  </div>
                  {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {[...Array(6)].map((_, i) => (
                        <div key={i} className="animate-pulse">
                          <div className="aspect-[4/3] bg-muted" />
                          <div className="p-6 space-y-4">
                            <div className="h-4 bg-muted w-1/4" />
                            <div className="h-6 bg-muted w-1/2" />
                            <div className="h-4 bg-muted w-3/4" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                      {featuredCars.map((car, index) => (
                        <CarCard key={car.id} car={car} index={index} />
                      ))}
                    </div>
                  )}
                </div>
              </section>
            );

          case 'brands':
            return s.module_brands_enabled !== false && <BrandsSection key="brands" settings={s} />;

          case 'about':
            return s.module_about_enabled !== false && (
              <section key="about" id="about" className="py-16 md:py-24 bg-card" data-testid="about-section">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
                    <motion.div
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8 }}
                      className="relative"
                    >
                      <img
                        src={s.about_image_url ? getImageUrl(s.about_image_url) : "https://images.unsplash.com/photo-1545276133-d91bcba0803c?w=800&q=80"}
                        alt="Showroom"
                        className="w-full aspect-[4/3] object-cover"
                      />
                      <div className="absolute -bottom-6 -right-6 w-48 h-48 border border-gold -z-10" />
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, x: 30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                    >
                      <p className="overline mb-4">{s.about_subtitle || 'Über uns'}</p>
                      <h2 className="font-heading text-4xl md:text-5xl font-light mb-6">
                        {(s.about_title || 'Automotive Excellence').split('\n').map((line, i) => (
                          <span key={i}>
                            {i > 0 && <br />}
                            {i === 1 ? <span className="italic">{line}</span> : line}
                          </span>
                        ))}
                      </h2>
                      <div className="space-y-4 text-muted-foreground font-light leading-relaxed">
                        <p>{s.about_text || 'Wir sind Ihr Partner für Premium-Fahrzeuge. Mit jahrelanger Erfahrung und Leidenschaft für außergewöhnliche Automobile bieten wir Ihnen einen erstklassigen Service.'}</p>
                      </div>
                      <div className="grid grid-cols-3 gap-8 mt-10 pt-10 border-t border-border">
                        <div>
                          <span className="font-heading text-3xl md:text-4xl text-primary">{s.stat_years || '20+'}</span>
                          <p className="overline mt-2">{s.stat_years_label || 'Jahre Erfahrung'}</p>
                        </div>
                        <div>
                          <span className="font-heading text-3xl md:text-4xl text-primary">{s.stat_cars || '500+'}</span>
                          <p className="overline mt-2">{s.stat_cars_label || 'Verkaufte Autos'}</p>
                        </div>
                        <div>
                          <span className="font-heading text-3xl md:text-4xl text-primary">{s.stat_satisfaction || '100%'}</span>
                          <p className="overline mt-2">{s.stat_satisfaction_label || 'Zufriedenheit'}</p>
                        </div>
                      </div>
                    </motion.div>
                  </div>
                </div>
              </section>
            );

          case 'video':
            const videoLightMode = s.video_light_mode === true;
            const videoId = extractVideoId(s.video_url);
            // Use YouTube thumbnail if no custom thumbnail is set
            const videoThumbnail = s.video_thumbnail_url 
              ? getImageUrl(s.video_thumbnail_url) 
              : (videoId ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg` : "https://images.unsplash.com/photo-1617531653332-bd46c24f2068?w=1200&q=80");
            
            return s.module_video_enabled !== false && (
              <section 
                key="video" 
                id="video" 
                className={`py-16 md:py-24 ${videoLightMode ? 'bg-theme-surface' : 'bg-[#0A0A0A]'}`}
                data-testid="video-section"
              >
                <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                  <div className="text-center mb-12">
                    <p className="overline text-primary mb-2">{s.video_subtitle || 'Erleben'}</p>
                    <h2 className={`font-heading text-4xl md:text-5xl font-light ${videoLightMode ? 'text-theme' : 'text-white'}`}>
                      {s.video_title || 'Die Kunst des Fahrens'}
                    </h2>
                  </div>
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="relative aspect-video max-w-5xl mx-auto rounded-lg overflow-hidden"
                  >
                    {videoPlaying && videoId ? (
                      // Embedded YouTube player with controls
                      <iframe
                        src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0`}
                        className="absolute inset-0 w-full h-full"
                        frameBorder="0"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        title="Video Player"
                      />
                    ) : (
                      // Thumbnail with play button
                      <div 
                        className="relative w-full h-full cursor-pointer group"
                        onClick={handleVideoClick}
                      >
                        <img
                          src={videoThumbnail}
                          alt="Video Thumbnail"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            // Fallback to hqdefault if maxresdefault doesn't exist
                            if (videoId && e.target.src.includes('maxresdefault')) {
                              e.target.src = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
                            }
                          }}
                        />
                        <div className="absolute inset-0 bg-black/30 group-hover:bg-black/40 transition-colors" />
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Play size={32} className="text-white ml-1" fill="white" />
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </div>
              </section>
            );

          case 'services':
            return s.module_services_enabled !== false && <ServicesSection key="services" settings={s} />;

          case 'team':
            return s.module_team_homepage_enabled && <TeamSection key="team" settings={s} />;

          case 'blog':
            return s.module_blog_homepage_enabled && <BlogSection key="blog" settings={s} posts={blogPosts} />;

          case 'newsletter':
            return s.module_newsletter_enabled !== false && <NewsletterSection key="newsletter" />;

          case 'contact':
            return s.module_contact_enabled !== false && (
              <section key="contact" id="contact" className="py-16 md:py-24" data-testid="contact-section">
                <div className="max-w-[1600px] mx-auto px-6 md:px-12">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6 }}
                    >
                      <p className="overline mb-4">{s.contact_subtitle || 'Kontakt'}</p>
                      <h2 className="font-heading text-4xl md:text-5xl font-light mb-6">
                        {s.contact_title || 'Kontaktieren Sie uns'}
                      </h2>
                      <p className="text-muted-foreground font-light mb-10 max-w-md">
                        {s.contact_text || 'Haben Sie Fragen zu einem Fahrzeug oder möchten Sie einen Besichtigungstermin vereinbaren? Wir sind für Sie da.'}
                      </p>
                      <div className="space-y-6">
                        <div>
                          <p className="overline mb-2">Adresse</p>
                          <p className="text-lg">{s.contact_address || 'Musterstraße 123, 1010 Wien'}</p>
                        </div>
                        <div>
                          <p className="overline mb-2">Telefon</p>
                          <p className="text-lg">{s.contact_phone || '+43 1 234 5678'}</p>
                        </div>
                        <div>
                          <p className="overline mb-2">E-Mail</p>
                          <p className="text-lg">{s.contact_email || 'info@tim-automobile.at'}</p>
                        </div>
                      </div>
                    </motion.div>
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.2 }}
                    >
                      <form onSubmit={handleContactSubmit} className="bg-card p-8 md:p-10 border border-border">
                        <div className="space-y-6">
                          <div>
                            <label className="overline block mb-2">Name</label>
                            <input
                              type="text"
                              value={contactForm.name}
                              onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                              className="input-premium w-full"
                              placeholder="Ihr Name"
                              required
                              data-testid="contact-name"
                            />
                          </div>
                          <div>
                            <label className="overline block mb-2">E-Mail</label>
                            <input
                              type="email"
                              value={contactForm.email}
                              onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                              className="input-premium w-full"
                              placeholder="ihre@email.at"
                              required
                              data-testid="contact-email"
                            />
                          </div>
                          <div>
                            <label className="overline block mb-2">Nachricht</label>
                            <textarea
                              value={contactForm.message}
                              onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                              className="input-premium w-full h-32 resize-none"
                              placeholder="Erzählen Sie uns von Ihrem Traumauto..."
                              required
                              data-testid="contact-message"
                            />
                          </div>
                          {/* Turnstile CAPTCHA */}
                          <TurnstileWidget 
                            onVerify={(token) => setTurnstileToken(token)}
                            onExpire={() => setTurnstileToken(null)}
                          />
                          <Button 
                            type="submit" 
                            className="btn-primary w-full flex items-center justify-center gap-2"
                            disabled={sending}
                            data-testid="contact-submit"
                          >
                            {sending ? (
                              <div className="spinner" />
                            ) : (
                              <>
                                Nachricht senden
                                <Send size={16} />
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    </motion.div>
                  </div>
                </div>
              </section>
            );

          case 'faq':
            return s.module_faq_enabled !== false && <FAQSection key="faq" settings={s} />;

          default:
            return null;
        }
      })}

      <Footer />
    </div>
  );
}
